import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { RouteFixedFareFormData } from "../../types/routeFixedFare";

export default function RouteFixedFareForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<RouteFixedFareFormData>({
    offerName: "",
    vehicleType: "",
    fromLocation: "",
    fromPostCode: "",
    toLocation: "",
    toPostCode: "",
    fare: 0,
    status: true,
    remarks: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEditMode) {
      fetchFareData();
    }
  }, [id]);

  const fetchFareData = async () => {
    try {
      const response = await axiosInstance.get(`/RouteFixedFare/${id}`);
      setFormData(response.data);
    } catch (error) {
      setError("Failed to fetch route fixed fare data");
      console.error("Error fetching route fixed fare:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Format the data for submission
    const submissionData = {
      ...formData,
      fare: Number(formData.fare), // Ensure fare is a number
      status: Boolean(formData.status) // Ensure status is a boolean
    };

    try {
      if (isEditMode) {
        await axiosInstance.put(`/RouteFixedFare/${id}`, submissionData);
      } else {
        await axiosInstance.post("/RouteFixedFare", submissionData);
      }
      navigate("/route-fixed-fares");
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to save route fixed fare");
      console.error("Error saving route fixed fare:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' 
        ? (e.target as HTMLInputElement).checked 
        : type === 'number'
          ? parseFloat(value) || 0
          : value
    }));
  };

  return (
    <div className="mx-auto px-4 py-8">
      <PageBreadcrumb pageTitle={isEditMode ? "Edit Route Fixed Fare" : "Add Route Fixed Fare"} />

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        {error && (
          <div className="mb-4 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            <InputWithLabel
              label="From Location"
              name="fromLocation"
              value={formData.fromLocation || ''}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="To Location"
              name="toLocation"
              value={formData.toLocation || ''}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="From Postcode"
              name="fromPostCode"
              value={formData.fromPostCode || ''}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="To Postcode"
              name="toPostCode"
              value={formData.toPostCode || ''}
              onChange={handleInputChange}
              required
            />

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Vehicle Type
              </label>
              <select
                name="vehicleType"
                value={formData.vehicleType || ''}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Vehicle Type</option>
                <option value="standard">Standard</option>
                <option value="executive">Executive</option>
                <option value="mpv">MPV</option>
                <option value="minibus">Minibus</option>
              </select>
            </div>

            <InputWithLabel
              label="Fare (£)"
              name="fare"
              type="number"
              step="0.01"
              value={formData.fare?.toString() || ''}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="Offer Name"
              name="offerName"
              value={formData.offerName || ''}
              onChange={handleInputChange}
            />

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="status"
                name="status"
                checked={formData.status}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="status" className="text-sm font-medium text-gray-700">
                Active
              </label>
            </div>

            <div className="space-y-2 md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Remarks
              </label>
              <textarea
                name="remarks"
                value={formData.remarks || ''}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/route-fixed-fares")}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 